import { Component, computed, DestroyRef, EventEmitter, input, Input, NgZone, Output, ViewChild } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import {
  EditorColorPickerComponent,
  EditorComponent as KendoEditorComponent,
  EditorModule,
  EditorPasteEvent,
  EditorView,
  Plugin,
  PluginKey,
  schema
} from '@progress/kendo-angular-editor';
import { TextSelection } from 'prosemirror-state';
import { EditorFontSizeComponent } from './custom-tools/editor-font-size/editor-font-size.component';
import {
  alignCenterIcon,
  alignJustifyIcon,
  alignLeftIcon,
  alignRightIcon,
  chevronDownIcon,
  clearCssIcon,
  codeSnippetIcon,
  dropletIcon,
  foregroundColorIcon,
  strikethroughIcon,
  subscriptIcon,
  supscriptIcon,
  tableAddIcon,
  unlinkIcon
} from '@progress/kendo-svg-icons';
import { FormatItem } from '@progress/kendo-angular-editor/common/format-item.interface';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { DocumentEventsService } from '../../services/document-events/document-events.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, Observable, of, switchMap, take } from 'rxjs';
import { DOMParser as ProseDOMParser, DOMSerializer } from 'prosemirror-model';
import { AlertService } from '../../../features/bizzmine/alerts/alert.service';
import { TranslationService } from '../../../core/services/translation/translation.service';
import { ColorPickerView } from '@progress/kendo-angular-inputs/colorpicker/models/colorpicker-view';
import { BizzColorPaletteSettings } from '../../constants/color-palette-settings';
import { PaletteSettings } from '@progress/kendo-angular-inputs/colorpicker/models/palette-settings';

@Component({
  selector: 'bizz-editor',
  standalone: true,
  imports: [CommonModule, EditorModule, ButtonModule, EditorFontSizeComponent],
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss']
})
export class EditorComponent {

  @ViewChild('editor') public editor: KendoEditorComponent;
  //@ViewChild('editor',{read: ElementRef}) public editorRef: ElementRef;
  @ViewChild('foreColorPicker') public foreColorPicker: EditorColorPickerComponent;
  @ViewChild('backColorPicker') public backColorPicker: EditorColorPickerComponent;

  @ViewChild('editorFontSizeTool') public editorFontSizeTool: EditorFontSizeComponent;

  public readonly BizzColorPaletteSettings: PaletteSettings = BizzColorPaletteSettings;
  public readonly views: ColorPickerView[] = ['palette', 'gradient'];
  public isMobileView = false;
  public chevronDownIcon = chevronDownIcon;

  @Input() public value: string;
  @Output() public valueChange = new EventEmitter<string>();
  @Input() public hideToolbar: boolean = false;
  @Input() public alwaysShowToolbar: boolean = false;
  @Input() public overflowToolbar: boolean = true;
  @Input() public readOnly: boolean = false;
  public minimumHeight = input<number>(200);
  @Input() public id: string = '';
  @Input() public isError: boolean = false;
  @Input() public isAiError: boolean | undefined = false;
  @Input() public disabled: boolean = false;
  //Warning: setting iframe to true, isolates the content preventing fonts and styles from the main app from being inherited.
  @Input() public iframe: boolean = false;
  @Input() public editorClass: string = '';
  @Output() public onBlur: EventEmitter<void> = new EventEmitter();

  public resizable = computed(() => {
    let mh = this.minimumHeight();
    return { minHeight: mh, maxHeight: Math.max(mh, 800) };
  });

  public data = [
    {
      text: 'Clean formatting',
      svgIcon: clearCssIcon,
      click: (): void => this.editor.exec('cleanFormatting')
    },
    {
      text: 'Strikethrough',
      svgIcon: strikethroughIcon,
      click: (): void => this.editor.exec('strikethrough')
    },
    {
      text: 'Subscript',
      svgIcon: subscriptIcon,
      click: (): void => this.editor.exec('subscript')
    },
    {
      text: 'Superscript',
      svgIcon: supscriptIcon,
      click: (): void => this.editor.exec('superscript')
    },
    {
      text: 'Foreground color',
      svgIcon: foregroundColorIcon,
      click: (): void => this.foreColorPicker.openDialog()
    },
    {
      text: 'Background color',
      svgIcon: dropletIcon,
      click: (): void => this.backColorPicker.openDialog()
    },
    {
      text: 'Align text left',
      svgIcon: alignLeftIcon,
      click: (): void => this.editor.exec('alignLeft')
    },
    {
      text: 'Align text center',
      svgIcon: alignCenterIcon,
      click: (): void => this.editor.exec('alignCenter')
    },
    {
      text: 'Align text right',
      svgIcon: alignRightIcon,
      click: (): void => this.editor.exec('alignRight')
    },
    {
      text: 'Align text justify',
      svgIcon: alignJustifyIcon,
      click: (): void => this.editor.exec('alignJustify')
    },
    {
      text: 'Unlink',
      svgIcon: unlinkIcon,
      click: (): void => this.editor.exec('unlink')
    },
    {
      text: 'Insert Table',
      svgIcon: tableAddIcon,
      click: (): void => this.editor.exec('insertTable', { rows: 1, cols: 1 })
    },
    {
      text: 'View source',
      svgIcon: codeSnippetIcon,
      click: (): void => this.editor.openDialog('viewSource')
    }
  ];
  public pasteCleanupSettings = {
    convertMsLists: true,
    removeHtmlComments: true,
    removeMsClasses: true,
    removeMsStyles: true,
    removeInvalidHTML: true
  };
  //public inFocus = false;
  public hasBeenFocus = false;
  private readonly MAX_IMAGE_SIZE_TO_EMBED = 15728640; // 15MB
  private fontSizes: { [key: string]: string } | null = null;
  private unInterceptedHttp: HttpClient;
  // @ts-ignore
  private detectFontSizePlugin = new Plugin({
    key: new PluginKey('detect-font-size'),
    view: (): { update: (view: EditorView) => void } => ({
      update: (view: EditorView): void => {
        // Details about the API and usage of the EditorView object are available in the
        // ProseMirror documentation - https://prosemirror.net/docs/ref/#view
        const state = view.state;
        const selection = state.selection as TextSelection;
        if (selection && selection.$cursor) {
          const $cursor = selection.$cursor;
          const node = this.getDeepestChild(view.domAtPos($cursor.pos).node);
          if (node) {
            const fontSize = this.getNodeFontSize(node);
            if (fontSize) {
              this.ngZone.run(() => {
                if (this.editorFontSizeTool)
                  this.editorFontSizeTool.value = fontSize;
              });
            }
          }
        }
      }
    })

  });

  public constructor(private ngZone: NgZone,
                     public themeService: ThemeService,
                     private destroyRef: DestroyRef,
                     private documentEventsService: DocumentEventsService,
                     private httpBackend: HttpBackend,
                     private alertService: AlertService,
                     private translationService: TranslationService) {
    //Create a new HttpClient instance with the HttpBackend
    // to avoid the api interceptor and handle errors quietly.
    this.unInterceptedHttp = new HttpClient(httpBackend);

    this.themeService.currentTheme$.pipe(take(1)).subscribe(() => {
      this.fontSizes = this.themeService.getThemeFontSizes();
    });

    this.documentEventsService.isMobile.pipe(
      takeUntilDestroyed(this.destroyRef)).subscribe((isMobile: boolean) => {
        this.isMobileView = isMobile;
      }
    );
    /* currently have show toolbar on focus (forever) v2 should be show only on focus -> this is partialy code for that
    https://bizzmine.slack.com/archives/G1N0DSE03/p1742901689171019
    if the link is unavailable you can blame René and Guillaume - i am innocent!
    this.documentEventsService.documentClickEvent.pipe(takeUntilDestroyed(this.destroyRef),filter(() => this.inFocus)).subscribe(

      (event: Event) => {
        if (event?.target && !this.editorRef.nativeElement.contains(event.target)) {
          this.ngZone.run(() => {
            this.inFocus = false;
          });
        }
      });*/
  }

  public editorPlugins = (args: Plugin[]): Plugin[] => [
    ...args,
    this.detectFontSizePlugin
  ];

  public getDeepestChild(node: Node | null): Node | null {
    if (node == null)
      return null;
    let child = node;
    while (child.firstChild != null && child.firstChild instanceof HTMLElement) {
      child = child.firstChild;
    }
    return child;
  }

  public getNodeFontSize(node: Node | HTMLElement | null, nrOfIterations = 5): string {
    if (nrOfIterations == 0)
      return '';
    if (node && node instanceof HTMLElement) {
      return window.getComputedStyle(node).getPropertyValue('font-size');
    } else if (node && (node as Node).parentNode != null) {
      return this.getNodeFontSize((node as Node).parentNode, nrOfIterations - 1);
    }
    return '';
  }

  public setSelection(): void {
    this.editor.view.state.selection;
  }

  public insertHtml(htmlElement: Element): void {
    const currState = this.editor.view.state;
    const tmpNode = document.createElement('div');
    tmpNode.innerHTML = htmlElement.outerHTML;

    const parser = ProseDOMParser.fromSchema(schema);
    const newNodes = parser.parse(tmpNode);

    this.editor.view.updateState(this.editor.view.state.apply(
      currState.tr.insert(currState.selection.head, newNodes.content)));
    //Update the editor value to reflect the changes
    this.editor.value = this.getHtml();
    this.onValueChange(this.editor.value);
  }

  public focus(): void {
    this.editor.focus();
  }

  public onValueChange(value: string | any): void {
    this.valueChange.emit(value);
  }

  public onPaste(event: EditorPasteEvent | ClipboardEvent): void {
    //Kendo is not consistent in the types of events..
    const clipboard = (event as ClipboardEvent)?.clipboardData;
    const cleanedHtml = (event as EditorPasteEvent)?.cleanedHtml;
    if (cleanedHtml) {
      const parser = new DOMParser();
      const pasteEl = parser.parseFromString(cleanedHtml, 'text/html');

      // Recursively iterate over all the children of the pasted element and retrieve the img urls with matching elements
      const imgUrlPairs: { element: HTMLImageElement, src: string }[] = this.getImgUrls(pasteEl.body);
      if (imgUrlPairs.length === 0)
        return;

      //If the pasted content contains images, prevent the default paste behavior and replace the src urls with base64 strings
      event.preventDefault();
      this.replaceSrcUrlsWithBase64Strings(pasteEl.body, imgUrlPairs);
    } else if (clipboard) {
      //Note FF only sypports pasting one file at a time: 😥 https://bugzilla.mozilla.org/show_bug.cgi?id=864052
      const files = Array.from(clipboard.files)
        .filter(item => item.type.startsWith('image'));
      const totalSize = files.reduce((acc, file) => acc + file.size, 0);
      if (totalSize > this.MAX_IMAGE_SIZE_TO_EMBED) {
        console.error(`Pasted images are too large and cannot be embedded as base64 src.`);
        this.alertService.setAlert({
          type: 'error',
          content: [this.translationService.translate('PastedContentTooLargeDescription')],
          title: this.translationService.translate('PastedContentTooLarge'),
          icon: 'triangle-exclamation',
          dismissable: true,
          timed: true
        });
        return;
      }
      if (files && files.length > 0) {
        event.preventDefault();
        this.insertFiles(files);
      }
    }
  }

  public onFormatChange(newFormat: FormatItem): void {
    if (this.editorFontSizeTool == null || this.fontSizes == null)
      return;
    const { tag } = newFormat;
    const fontSize = this.fontSizes[tag];
    if (fontSize) {
      this.editorFontSizeTool.value = fontSize;
      this.editor.focus();
      this.editor.exec('cleanFormatting', {
        blocksInSelection: false
      });
    }
  }

  private getHtml(): string {
    const currState = this.editor.view.state;
    const div = document.createElement('div');
    const fragment = DOMSerializer
      .fromSchema(schema)
      .serializeFragment(currState.doc.content);

    div.appendChild(fragment);

    return div.innerHTML;
  }

  /**
   * Recursively iterate over all the children of the pasted element and retrieve the img urls with matching elements
   * @param element The element to start the search from
   * @returns An array of objects containing the image element and the src url
   **/
  private getImgUrls(element: Element): { element: HTMLImageElement, src: string }[] {
    let imgUrls: { element: HTMLImageElement, src: string }[] = [];
    if (element.tagName === 'IMG') {
      imgUrls.push({ element: (element as HTMLImageElement), src: element.getAttribute('src') ?? '' });
    }
    if (element.children.length > 0) {
      Array.from(element.children).forEach((child: Element) => {
        imgUrls = imgUrls.concat(this.getImgUrls(child));
      });
    }
    return imgUrls;
  }

  /**
   * Iterates over the img urls and retrieves the blobs of the images,
   * loads the blobs as base64 strings and replaces the src urls with the base64 strings
   * Finally inserts the pasted html into the editor
   * @param pastedHtml - HtmlElement to paste
   * @param imgUrlPairs - pairs of image elements and their src urls
   */
  private replaceSrcUrlsWithBase64Strings(pastedHtml: HTMLElement, imgUrlPairs: {
    element: HTMLImageElement,
    src: string
  }[]): void {
    const observables = imgUrlPairs.map(pair => {
      return this.unInterceptedHttp.get(pair.element.src, { responseType: 'blob' }).pipe(
        catchError(() => {
          console.error(`Pasted image with url ${pair.src} couldn't be retrieved.`);
          return of(undefined);
        }),
        switchMap((blob: Blob | undefined) => {
          if (!blob)
            return of({ element: pair.element, url: pair.src });
          if (blob.size > this.MAX_IMAGE_SIZE_TO_EMBED) {
            console.warn(`Pasted image with url ${pair.src} is too large and not will not be converted to embedded base64 src.`);
            this.alertService.setAlert({
              type: 'error',
              content: [this.translationService.translate('PastedContentTooLargeDescription')],
              title: this.translationService.translate('PastedContentTooLarge'),
              icon: 'triangle-exclamation',
              dismissable: true,
              timed: true
            });
            return of({ element: pair.element, url: pair.src });
          }
          return new Observable<{ element: HTMLImageElement, url: string }>(subscriber => {
            const reader = new FileReader();
            const onLoadEnd = (): void => {
              subscriber.next({ element: pair.element, url: reader.result as string });
              reader.removeEventListener('onloadend', onLoadEnd);
              reader.removeEventListener('onerror', onError);
              subscriber.complete();
            };
            const onError = (): void => {
              console.error(`Pasted image with url ${pair.src} couldn't be embedded base64 src.`);
              subscriber.next({ element: pair.element, url: pair.src });
              reader.removeEventListener('onloadend', onLoadEnd);
              reader.removeEventListener('onerror', onError);
              subscriber.complete();
            };
            reader.onloadend = onLoadEnd;
            reader.onerror = onError;
            reader.readAsDataURL(blob);
          });
        }),
        catchError(() => {
          console.error(`Pasted image with url ${pair.src} couldn't be retrieved and embedded as base64 src.`);
          return of({ element: pair.element, url: pair.src });
        }));
    });

    forkJoin(observables).subscribe({
      next: results => {
        results.forEach(result => {
          if (result.url !== result.element.src) {
            result.element.setAttribute('src', result.url);
            result.element.setAttribute('srcset', '');
          }
        });
        this.insertHtml(pastedHtml);
      },
      error: err => {
        console.error('Error while converting pasted images to base64 src: ', err);
      }
    });
  }

  /**
   * Iterates over the (pasted) files, converts them to base64 strings and embeds them as img elements in the editor
   * @param files - files to embed
   */
  private insertFiles(files: (File | null)[]): void {
    const observables = files.map(file => {
      if (!file) {
        console.error('Pasted image is null.');
        return of('');
      } else if (file.size > this.MAX_IMAGE_SIZE_TO_EMBED) {
        console.warn(`Pasted image with url ${file.name} is too large and not converted to embedded base64 src.`);
        return of('');
      }

      return new Observable<string>((subscriber) => {
        const reader = new FileReader();
        const onLoadEnd = (): void => {
          subscriber.next(reader.result as string);
          reader.removeEventListener('loadend', onLoadEnd);
          reader.removeEventListener('error', onError);
          subscriber.complete();

        };
        const onError = reader.onerror = (): void => {
          console.error(`Pasted image with url ${file.name} couldn't be embedded base64 src.`);
          subscriber.next('');
          reader.removeEventListener('loadend', onLoadEnd);
          reader.removeEventListener('error', onError);
          subscriber.complete();
        };
        reader.onloadend = onLoadEnd;
        reader.onerror = onError;
        reader.readAsDataURL(file);
      });
    });

    forkJoin(observables).subscribe({
      next: results => {
        const pastedHtml = document.createElement('p');
        results.forEach(result => {
          if (result) {
            const img = document.createElement('img');
            img.src = result;
            pastedHtml.appendChild(img);
          }
        });
        this.insertHtml(pastedHtml);
      },
      error: err => {
        console.error('Error while converting pasted images to base64 src: ', err);
      }
    });
  }
}
